import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  Icon,
  useTheme,
  alpha,
  styled,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../modules/auth/hooks/useAuth";
import { DenominationCount } from "../SessionClose/DenominationCount";
import { LoadingButton } from "@mui/lab";

interface SessionStartProps {
  open: boolean;
  onClose: () => void;
  onSessionStart: (data: {
    initial_amount: number;
    denominations: {
      bills: Array<{ value: number; count: number }>;
      coins: Array<{ value: number; count: number }>;
    };
  }) => void;
  isLoading?: boolean;
}

const steps = ["Count Denominations", "Session Summary"];

const THEME_COLORS = {
  primary: '#1B7B6B',  // Deep teal from header
  primaryLight: '#1E8078', // Lighter teal for hover states
  primaryDark: '#166F68', // Darker teal for active states
  success: '#059669',
  danger: '#B91C1C',
  background: '#FFFFFF',
  text: {
    primary: '#1A202C',
    secondary: '#4A5568',
    muted: '#718096',
  }
};

const defaultDenominations = {
  bills: [
    { value: 20, count: 0 },
    { value: 10, count: 0 },
    { value: 5, count: 0 },
    { value: 1, count: 0 },
  ],
  coins: [
    { value: 0.5, count: 0 },
    { value: 0.1, count: 0 },
    { value: 0.05, count: 0 },
    { value: 0.025, count: 0 },
  ],
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.shape.borderRadius * 2,
    backgroundImage: "none",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    width: "95%",
    maxWidth: 1400,
    margin: theme.spacing(2),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: THEME_COLORS.primary,
  color: THEME_COLORS.background,
  padding: theme.spacing(3),
  "& .MuiTypography-root": {
    color: THEME_COLORS.background,
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: alpha(THEME_COLORS.primary, 0.04),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  "& .MuiStepLabel-root .Mui-active": {
    color: THEME_COLORS.primary,
  },
  "& .MuiStepLabel-root .Mui-completed": {
    color: THEME_COLORS.success,
  },
  "& .MuiStepConnector-line": {
    borderColor: alpha(THEME_COLORS.primary, 0.2),
  },
  "& .MuiStepIcon-root": {
    color: alpha(THEME_COLORS.primary, 0.2),
    "&.Mui-active": {
      color: THEME_COLORS.primary,
    },
    "&.Mui-completed": {
      color: THEME_COLORS.primary,
    },
  },
}));

const StyledStepContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: THEME_COLORS.primary,
    boxShadow: `0 4px 12px ${alpha(THEME_COLORS.primary, 0.08)}`,
  },
}));

const StyledActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  textTransform: "none",
  fontWeight: 500,
  transition: "all 0.2s ease-in-out",
  "&.MuiButton-contained": {
    backgroundColor: THEME_COLORS.primary,
    color: THEME_COLORS.background,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: THEME_COLORS.primaryLight,
      boxShadow: `0 4px 12px ${alpha(THEME_COLORS.primary, 0.2)}`,
    },
    "&:active": {
      backgroundColor: THEME_COLORS.primaryDark,
    },
  },
  "&.MuiButton-outlined": {
    borderColor: alpha(THEME_COLORS.primary, 0.5),
    color: THEME_COLORS.primary,
    "&:hover": {
      borderColor: THEME_COLORS.primary,
      backgroundColor: alpha(THEME_COLORS.primary, 0.04),
    },
  },
}));

const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  textTransform: "none",
  fontWeight: 500,
  transition: "all 0.2s ease-in-out",
  backgroundColor: THEME_COLORS.primary,
  color: THEME_COLORS.background,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: THEME_COLORS.primaryLight,
    boxShadow: `0 4px 12px ${alpha(THEME_COLORS.primary, 0.2)}`,
  },
  "&:active": {
    backgroundColor: THEME_COLORS.primaryDark,
  },
}));

export const SessionStart: React.FC<SessionStartProps> = ({
  open,
  onClose,
  onSessionStart,
  isLoading = false,
}) => {
  const { logout } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [denominationData, setDenominationData] = useState(defaultDenominations);

  const calculateTotal = () => {
    const billsTotal = denominationData.bills.reduce(
      (sum, bill) => sum + bill.value * bill.count,
      0
    );
    const coinsTotal = denominationData.coins.reduce(
      (sum, coin) => sum + coin.value * coin.count,
      0
    );
    return billsTotal + coinsTotal;
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      onSessionStart({
        initial_amount: calculateTotal(),
        denominations: denominationData,
      });
      setShowSuccessAlert(true);
      // Close the dialog and navigate after 2 seconds
      setTimeout(() => {
        setShowSuccessAlert(false);
        onClose();
        navigate("/pos/orders");
      }, 2000);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleDenominationSubmit = (data: {
    bills: Array<{ value: number; count: number }>;
    coins: Array<{ value: number; count: number }>;
  }) => {
    setDenominationData(data);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <DenominationCount
            initialData={denominationData}
            onSubmit={handleDenominationSubmit}
          />
        );
      case 1:
        return (
          <StyledStepContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.primary,
                display: "flex",
                alignItems: "center",
                gap: 1,
                pb: 1.5,
                borderBottom: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
                mb: 2,
              }}
            >
              <Icon>summarize</Icon>
              Session Summary
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Initial Cash Amount
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: THEME_COLORS.primary,
                  mt: 0.5,
                }}
              >
                BD {calculateTotal().toFixed(3)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.text.muted,
                  mt: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Icon sx={{ fontSize: 16 }}>info</Icon>
                Please verify the amount before starting the session
              </Typography>
            </Box>
          </StyledStepContent>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: { minHeight: "60vh" },
        }}
      >
        <StyledDialogTitle>
          <Box>
            <Typography 
              variant="h5" 
              component="div" 
              sx={{ 
                fontWeight: 600,
                mb: 1,
              }}
            >
              Initialize Session
            </Typography>
            <Typography
              variant="body2"
              sx={{ 
                opacity: 0.9,
              }}
            >
              Please follow the steps below to start your session
            </Typography>
          </Box>
        </StyledDialogTitle>
        <DialogContent sx={{ 
          p: 2,
          display: "flex",
          flexDirection: "column"
        }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: alpha(THEME_COLORS.primary, 0.02),
              borderRadius: 1,
              border: `1px solid ${alpha(THEME_COLORS.primary, 0.1)}`,
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(THEME_COLORS.primary, 0.1)}` }}>
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  color: THEME_COLORS.primary,
                  fontWeight: 500,
                }}
              >
                <Icon>assignment</Icon>
                Session Start Steps
              </Typography>
              <StyledStepper activeStep={activeStep}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>
                      <Typography sx={{ 
                        fontWeight: 500,
                        color: THEME_COLORS.text.primary,
                      }}>
                        {label}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </StyledStepper>
            </Box>

            <Box sx={{ p: 2 }}>
              {renderStepContent(activeStep)}
            </Box>
          </Paper>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <StyledActionButton
            startIcon={<Icon>logout</Icon>}
            onClick={logout}
            sx={{
              color: THEME_COLORS.danger,
              borderColor: alpha(THEME_COLORS.danger, 0.5),
              "&:hover": {
                borderColor: THEME_COLORS.danger,
                backgroundColor: alpha(THEME_COLORS.danger, 0.04),
              },
            }}
            variant="outlined"
          >
            Logout
          </StyledActionButton>
          <Box sx={{ flex: 1 }} />
          {activeStep > 0 && (
            <StyledActionButton
              startIcon={<Icon>arrow_back</Icon>}
              onClick={handleBack}
              variant="outlined"
            >
              Back
            </StyledActionButton>
          )}
          <StyledLoadingButton
            loading={isLoading}
            startIcon={
              activeStep === steps.length - 1 ? (
                <Icon>play_arrow</Icon>
              ) : (
                <Icon>arrow_forward</Icon>
              )
            }
            variant="contained"
            onClick={handleNext}
            size="large"
          >
            {activeStep === steps.length - 1 ? "Start Session" : "Next"}
          </StyledLoadingButton>
        </DialogActions>
      </StyledDialog>

      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: THEME_COLORS.success,
            color: THEME_COLORS.background,
            '& .MuiAlert-icon': {
              color: THEME_COLORS.background,
            },
            boxShadow: `0 4px 12px ${alpha(THEME_COLORS.success, 0.2)}`,
          }}
        >
          Session started successfully!
        </Alert>
      </Snackbar>
    </>
  );
};
